import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateProject, useGenerateSlug } from '@/hooks/use-projects';
import { useEmployees } from '@/hooks/use-employees';
import { useDepartments } from '@/hooks/use-organizations';
import { PROJECT_COLORS } from '@hygge/shared';
import type { DepartmentWithRelations, EmployeeListItem } from '@hygge/shared';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Check, ChevronDown, Loader2, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (id: string) => void;
}

export function CreateProjectDialog({ open, onOpenChange, onSuccess }: CreateProjectDialogProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [managerIds, setManagerIds] = useState<string[]>([]);
  // Creating a project with yourself as a manager is refused by the server
  // (somebody else appoints you), so you are not offered. A super_admin may.
  const me = useAuthStore((s) => s.user);
  const notOffered = me?.employee?.id && me.role !== 'super_admin' ? [me.employee.id] : [];
  const [color, setColor] = useState<string>(PROJECT_COLORS[0]);
  const [error, setError] = useState('');

  const createProject = useCreateProject();
  const generateSlug = useGenerateSlug();

  const { data: employeesData } = useEmployees({ limit: 500, statuses: ['active', 'probation'] });
  const employees = employeesData?.data ?? [];

  // Auto-generate slug when name changes (only if user hasn't manually edited it)
  useEffect(() => {
    if (!slugEdited && name.length >= 2) {
      generateSlug.mutate(name, {
        onSuccess: (generatedSlug) => setSlug(generatedSlug),
      });
    }
  }, [name, slugEdited]);

  const handleSlugChange = (value: string) => {
    setSlugEdited(true);
    setSlug(value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Project name is required');
      return;
    }
    if (!slug.trim()) {
      setError('Slug is required');
      return;
    }
    if (managerIds.length === 0) {
      setError('Please select at least one project manager');
      return;
    }

    createProject.mutate(
      { name: name.trim(), slug, description: description.trim() || undefined, managerIds, color },
      {
        onSuccess: (project) => {
          resetForm();
          onOpenChange(false);
          onSuccess?.(project.id);
        },
        onError: (err) => {
          setError(extractErrorMessage(err, 'Failed to create project'));
        },
      },
    );
  };

  const resetForm = () => {
    setName('');
    setSlug('');
    setSlugEdited(false);
    setDescription('');
    setManagerIds([]);
    setColor(PROJECT_COLORS[0]);
    setError('');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) resetForm();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Project</DialogTitle>
          <DialogDescription>Create a new project and assign a manager.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Project Name</Label>
            <Input
              id="name"
              placeholder="e.g. AeyeOS"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="slug">Slug (Task Key Prefix)</Label>
              <Input
                id="slug"
                placeholder="e.g. AEYE"
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                maxLength={6}
                className="font-mono uppercase"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex items-center gap-1.5 pt-1">
                {PROJECT_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={cn(
                      'h-7 w-7 rounded-full border-2 transition-all cursor-pointer',
                      color === c ? 'border-foreground scale-110' : 'border-transparent hover:scale-105',
                    )}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Project Managers</Label>
            <ManagerPicker
              employees={employees}
              notOffered={notOffered}
              value={managerIds}
              onChange={setManagerIds}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder="Brief project description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="cursor-pointer">
              Cancel
            </Button>
            <Button type="submit" disabled={createProject.isPending} className="cursor-pointer">
              {createProject.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Project
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Somebody the picker can show: an employee from the list, or a known manager who is not in it. */
type PickerPerson = Pick<EmployeeListItem, 'id' | 'firstName' | 'lastName' | 'avatarUrl'> &
  Partial<Pick<EmployeeListItem, 'position' | 'department'>>;

export function ManagerPicker({
  employees,
  value,
  onChange,
  known = [],
  notOffered = [],
}: {
  employees: EmployeeListItem[];
  value: string[];
  onChange: (ids: string[]) => void;
  /**
   * People already chosen who may be missing from `employees` — a manager on
   * onboarding, or past the page the list loaded. Without them their chip
   * vanished while their id stayed in `value`, so they could not be removed.
   */
  known?: PickerPerson[];
  /**
   * Ids not offered for picking, unless already chosen: the caller, who may not
   * appoint themselves — the server refuses it and the whole save with it.
   */
  notOffered?: string[];
}) {
  const formatName = useEmployeeNameFormatter();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: departments } = useDepartments();

  // Default to "Project Management" department
  const pmDept = useMemo(
    () => departments?.find((d: DepartmentWithRelations) => d.name.toLowerCase().includes('project management')),
    [departments],
  );
  const [departmentId, setDepartmentId] = useState<string>('');

  // Set default department once departments load
  useEffect(() => {
    if (pmDept && !departmentId) {
      setDepartmentId(pmDept.id);
    }
  }, [pmDept, departmentId]);

  const pool = useMemo<PickerPerson[]>(() => {
    const ids = new Set(employees.map((e) => e.id));
    return [...employees, ...known.filter((k) => !ids.has(k.id))];
  }, [employees, known]);

  const filtered = useMemo(() => {
    let list = pool.filter((e) => !notOffered.includes(e.id) || value.includes(e.id));
    if (departmentId && departmentId !== 'all') {
      list = list.filter((e) => e.department?.id === departmentId);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          // Formatted (displayed) name plus raw first-last covers both orders and any single-part substring.
          formatName({ firstName: e.firstName, lastName: e.lastName }).toLowerCase().includes(q) ||
          `${e.firstName} ${e.lastName}`.toLowerCase().includes(q),
      );
    }
    // The chosen are always on the list, whatever the filters, so each can be unticked.
    const shownIds = new Set(list.map((e) => e.id));
    const chosen = pool.filter((e) => value.includes(e.id) && !shownIds.has(e.id));
    return [...chosen, ...list];
  }, [pool, notOffered, value, departmentId, search, formatName]);

  const selectedSet = new Set(value);
  const selectedEmployees = value
    .map((id) => pool.find((e) => e.id === id))
    .filter((e): e is PickerPerson => !!e);

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal cursor-pointer h-auto min-h-9 py-1.5"
        >
          {selectedEmployees.length === 0 ? (
            <span className="text-muted-foreground">Select managers...</span>
          ) : (
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="flex -space-x-1.5">
                {selectedEmployees.slice(0, 3).map((emp) => (
                  <Avatar key={emp.id} className="h-5 w-5 ring-1 ring-background">
                    {emp.avatarUrl && <AvatarImage src={emp.avatarUrl} />}
                    <AvatarFallback className="text-[8px]">
                      {emp.firstName[0]}{emp.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <span className="truncate text-sm">
                {formatName({ firstName: selectedEmployees[0].firstName, lastName: selectedEmployees[0].lastName })}
                {selectedEmployees.length > 1 && ` +${selectedEmployees.length - 1}`}
              </span>
            </div>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        {/* Department filter */}
        <div className="p-2 border-b border-border">
          <Select value={departmentId} onValueChange={setDepartmentId}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="All departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departments?.map((d: DepartmentWithRelations) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Search */}
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        {/* Employee list */}
        <div className="max-h-52 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <div className="py-4 text-center text-xs text-muted-foreground">
              No employees found
            </div>
          ) : (
            filtered.map((emp) => {
              const isSelected = selectedSet.has(emp.id);
              return (
                <button
                  key={emp.id}
                  type="button"
                  className={cn(
                    'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors',
                    isSelected ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50',
                  )}
                  onClick={() => toggle(emp.id)}
                >
                  <Avatar className="h-6 w-6 shrink-0">
                    {emp.avatarUrl && <AvatarImage src={emp.avatarUrl} />}
                    <AvatarFallback className="text-[9px]">
                      {emp.firstName[0]}{emp.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-xs font-medium truncate">
                      {formatName({ firstName: emp.firstName, lastName: emp.lastName })}
                    </div>
                    {emp.position?.title && (
                      <div className="text-[11px] text-muted-foreground truncate">
                        {emp.position.title}
                      </div>
                    )}
                  </div>
                  {isSelected && <Check className="h-3.5 w-3.5 text-hygge shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
